import React from 'react';
import styled from 'styled-components';
import { selectChevron } from './fieldControls';

/**
 * Category filter chips shared by the Practitioner and Service pickers:
 * "All" plus one chip per category that actually has something in the
 * list. `variant: 'profile'` matches the practitioner picker's Profile
 * cards (reads its `--pp-accent`); 'compact' matches the default chips.
 * `display: 'dropdown'` shows the same choice as a select instead.
 */

/**
 * The category id of a professional / service / professional service,
 * whichever shape the backend sent (`categoryId`, `serviceCategoryId`, a
 * `category` object, or a bare `category` id). Null when uncategorised.
 * @returns {number|string|null}
 */
export function categoryIdOf(item) {
  if (!item) return null;
  if (item.categoryId != null) return item.categoryId;
  if (item.serviceCategoryId != null) return item.serviceCategoryId;
  if (item.category && typeof item.category === 'object') return item.category.id ?? null;
  return item.category ?? null;
}

/** The category name an item carries itself, when the backend joined it. */
function categoryNameOf(item) {
  if (item?.serviceCategoryName) return item.serviceCategoryName;
  if (item?.category && typeof item.category === 'object') return item.category.name || null;
  return item?.categoryName || null;
}

/**
 * Categories (of `type`, or untyped) that at least one item belongs to, in
 * the location's own category order — plus any an item names itself that
 * the list doesn't have (e.g. no category list was available).
 */
export function categoriesInUse(categories, items, type) {
  const used = new Set(items.map((item) => String(categoryIdOf(item))));
  const listed = (categories || []).filter(
    (category) => (!category.type || category.type === type) && used.has(String(category.id))
  );
  const known = new Set(listed.map((category) => String(category.id)));
  const extra = [];
  items.forEach((item) => {
    const id = categoryIdOf(item);
    const name = categoryNameOf(item);
    if (id == null || !name || known.has(String(id))) return;
    if ((categories || []).some((category) => String(category.id) === String(id))) return;
    known.add(String(id));
    extra.push({ id, name });
  });
  return [...listed, ...extra];
}

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ $profile }) => ($profile ? '10px' : '8px')};
  margin-bottom: ${({ $profile }) => ($profile ? '24px' : '12px')};
`;

const Chip = styled.button`
  font-family: inherit;
  cursor: pointer;
  ${({ $profile, $active }) =>
    $profile
      ? `
    min-height: 38px;
    padding: 0 20px;
    font-size: 0.95rem;
    color: ${$active ? '#fff' : '#3d4e5c'};
    background: ${$active ? 'var(--pp-accent)' : 'color-mix(in srgb, var(--pp-accent) 14%, #fff)'};
    border: none;
    border-radius: 999px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  `
      : `
    min-height: 32px;
    padding: 0 12px;
    font-size: 0.82rem;
    font-weight: 600;
    color: ${$active ? '#fff' : 'inherit'};
    background: ${$active ? 'var(--flow-primary, #0d6efd)' : 'transparent'};
    border: 1px solid ${$active ? 'var(--flow-primary, #0d6efd)' : 'var(--flow-border, #dee2e6)'};
    border-radius: 999px;
  `}

  &:focus-visible {
    outline: 2px solid var(--flow-primary, #0d6efd);
    outline-offset: 2px;
  }
`;

const CategorySelect = styled.select`
  width: 100%;
  min-height: 44px;
  margin-bottom: ${({ $profile }) => ($profile ? '24px' : '12px')};
  padding: 0 12px;
  border: 1px solid var(--flow-border, #dee2e6);
  border-radius: var(--flow-radius, 8px);
  font-size: 0.95rem;
  font-family: inherit;
  color: var(--flow-text, #1a1a1a);
  background-color: var(--flow-surface, #f8f9fa);
  cursor: pointer;
  ${selectChevron}

  &:focus-visible {
    outline: 2px solid var(--flow-primary, #0d6efd);
    outline-offset: 1px;
  }
`;

/**
 * @param {{ categories: {id: number|string, name: string}[], activeId: number|string|null,
 *   onChange: (id: number|string|null) => void, variant?: 'compact'|'profile',
 *   display?: 'chips'|'dropdown', label?: string }} props
 */
const CategoryChips = ({
  categories,
  activeId,
  onChange,
  variant = 'compact',
  display = 'chips',
  label
}) => {
  if (!categories.length) return null;
  const profile = variant === 'profile';
  if (display === 'dropdown') {
    // Option values are strings; map back to the category's own id.
    const handleSelect = (event) => {
      const match = categories.find((category) => String(category.id) === event.target.value);
      onChange(match ? match.id : null);
    };
    return (
      <CategorySelect
        aria-label={label || 'Filter by category'}
        value={activeId == null ? '' : String(activeId)}
        onChange={handleSelect}
        $profile={profile}>
        <option value="">All categories</option>
        {categories.map((category) => (
          <option key={category.id} value={String(category.id)}>
            {category.name}
          </option>
        ))}
      </CategorySelect>
    );
  }
  return (
    <Row role="group" aria-label={label || 'Filter by category'} $profile={profile}>
      <Chip
        type="button"
        aria-pressed={activeId == null}
        $active={activeId == null}
        $profile={profile}
        onClick={() => onChange(null)}>
        All
      </Chip>
      {categories.map((category) => {
        const active = String(activeId) === String(category.id);
        return (
          <Chip
            key={category.id}
            type="button"
            aria-pressed={active}
            $active={active}
            $profile={profile}
            onClick={() => onChange(active ? null : category.id)}>
            {category.name}
          </Chip>
        );
      })}
    </Row>
  );
};

export default CategoryChips;
